export class ApiError extends Error {
    readonly status: number;
    readonly data: unknown;

    constructor(status: number, message: string, data?: unknown) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }

    get isUnauthorized(): boolean {
        return this.status === 401;
    }
}

type AmplifyRestError = {
    message?: string;
    response?: {
        statusCode?: number;
        body?: { json?: () => Promise<unknown> };
    };
    statusCode?: number;
};

export async function normalizeAmplifyError(error: unknown): Promise<ApiError> {
    if (error instanceof ApiError) return error;

    const err = error as AmplifyRestError;
    const status = err.response?.statusCode ?? err.statusCode ?? 500;

    let data: unknown;
    let message = err.message ?? 'Unknown API error';

    try {
        const bodyJson = await err.response?.body?.json?.();
        if (bodyJson !== undefined) {
            data = bodyJson;
            if (
                typeof bodyJson === 'object' &&
                bodyJson !== null &&
                'message' in bodyJson &&
                typeof (bodyJson as { message: unknown }).message === 'string'
            ) {
                message = (bodyJson as { message: string }).message;
            }
        }
    } catch {
        // Body may be empty or non-JSON — keep default message
    }

    return new ApiError(status, message, data);
}

export function normalizeAxiosError(error: unknown): ApiError {
    if (error instanceof ApiError) return error;

    if (
        typeof error === 'object' &&
        error !== null &&
        'isAxiosError' in error &&
        (error as { isAxiosError: boolean }).isAxiosError
    ) {
        const axiosError = error as {
            response?: { status?: number; data?: unknown };
            message?: string;
        };
        const status = axiosError.response?.status ?? 500;
        const data = axiosError.response?.data;
        const message =
            typeof data === 'object' &&
            data !== null &&
            'message' in data &&
            typeof (data as { message: unknown }).message === 'string'
                ? (data as { message: string }).message
                : (axiosError.message ?? 'Unknown API error');

        return new ApiError(status, message, data);
    }

    return new ApiError(
        500,
        error instanceof Error ? error.message : 'Unknown API error'
    );
}
